<script lang="ts" setup>
import { useField } from 'vee-validate'
import debounce from 'lodash/debounce'
import { DEBOUNCE_SEARCH } from '~/constants'

const props = defineProps({
  name: {
    type: String,
    required: true,
  },
  label: {
    type: String,
    required: false,
    default: '',
  },
  placeholder: {
    type: String,
    required: false,
    default: '',
  },
  disabled: {
    type: Boolean,
    required: false,
    default: false,
  },
  class: {
    type: String,
    required: false,
    default: '',
  },
})
const emit = defineEmits([
  'onSearchChange',
  'onFocus',
  'updateFocused',
  'onClear',
  'onInput',
])

const { value, errorMessage } = useField(() => props.name)

const appendIcon = computed(() => {
  return props.disabled ? 'icon-lock' : undefined
})

const onClear = () => {
  value.value = ''
  emit('onClear', '')
}

const debouncedSearch = debounce((search) => {
  emit('onSearchChange', search)
}, DEBOUNCE_SEARCH)

const onSearch = debounce((event: Event) => {
  const target = event.target as HTMLInputElement
  debouncedSearch(target.value)
  emit('onInput', target.value)
})

const onFocus = () => {
  emit('onFocus')
}

const isFocused = ref(false)
const updateFocused = (isFocus: boolean) => {
  emit('updateFocused')
  isFocused.value = isFocus
}

const isHovered = ref(false)
const variant = computed(() =>
  isHovered.value || isFocused.value ? 'outlined' : 'solo',
)
const isShowPlaceholder = computed(() => !isFocused.value)
</script>

<template>
  <div
    :class="{
      'form-input': true,
      [props.class]: Boolean(props.class),
    }"
    @mouseover="isHovered = true"
    @mouseleave="isHovered = false"
  >
    <label v-if="props.label" class="form-input__label" :for="props.name">{{
      props.label
    }}</label>
    <VTextField
      v-model="value"
      v-bind="{
        variant,
        placeholder: isShowPlaceholder ? props.placeholder : '',
      }"
      clearable
      density="compact"
      hide-details
      :error="Boolean(errorMessage)"
      :disabled="props.disabled"
      :append-inner-icon="appendIcon"
      @click:clear="onClear"
      @input="onSearch"
      @click:control="onFocus"
      @update:focused="updateFocused"
    >
      <template #prepend-inner>
        <i class="icon-search-game" />
      </template>
      <template #clear>
        <i class="icon-delete" @click="onClear" />
      </template>
    </VTextField>
    <span v-if="errorMessage" class="form-input__error-message">{{
      errorMessage
    }}</span>
  </div>
</template>
